import { createSelector } from 'reselect';
import cartUtils from '../utils/cart';
import { selectConfigurationContext } from './configurationContext';
import { RetouchingProduct } from '../components/v3/products/retouching/types';
import type {
  RetouchingOrderState,
  RetouchingRootState,
} from './retouchingState';

const EMPTY_LEVELS: RetouchingProduct[] = [];

const currentEventId = (state: RetouchingRootState) =>
  state.shoot?.shoot?.event_id || state.shoot?.shoot?.id;

// A null key means the order being built: the selected student at the current
// event, the same order the catalog and the post-add screen work on.
const resolveOrderKey = (
  state: RetouchingRootState,
  orderKey: string | null,
): string =>
  orderKey ??
  `${currentEventId(state)}-${state.additionalData?.selectedStudent?.id}`;

const selectOrderKey = (state: RetouchingRootState, orderKey: string | null) =>
  resolveOrderKey(state, orderKey);
const selectOrder = (state: RetouchingRootState, orderKey: string | null) =>
  state.v3Order?.orders?.[resolveOrderKey(state, orderKey)];

const belongsToCurrentEvent = (
  order: RetouchingOrderState | undefined,
  orderKey: string,
  shootEventId: unknown,
): boolean =>
  shootEventId != null &&
  String(order?.event?.id ?? orderKey.split('-')[0]) === String(shootEventId);

// The retouching levels that order can buy: its own snapshot when it has one,
// the shoot's list when it is on the current event, otherwise none. One entry
// per name, as the retouching screen shows them, and none at all when any
// level's price cannot be trusted (the screen refuses those too).
export const selectRetouchingLevelsForOrder = createSelector(
  selectOrder,
  selectOrderKey,
  currentEventId,
  (state: RetouchingRootState) => state.shoot?.retouchingProducts,
  (order, orderKey, shootEventId, shootProducts): RetouchingProduct[] => {
    const source =
      order?.productContext?.retouchingProducts ??
      (belongsToCurrentEvent(order, orderKey, shootEventId)
        ? shootProducts
        : null);
    if (!source) return EMPTY_LEVELS;

    const products: RetouchingProduct[] = (
      Array.isArray(source) ? source : [source]
    ).filter(Boolean);
    const levels = products.filter(
      (product, index) =>
        products.findIndex((candidate) => candidate.name === product.name) ===
        index,
    );
    const priced = levels.every((product) =>
      cartUtils.validRetouchingPrice({
        ...product,
        tiered_unit_prices_cents: product.tiered_unit_prices_cents || [],
      }),
    );
    return priced && levels.length ? levels : EMPTY_LEVELS;
  },
);

// The live company flag decides for the current event, the order's persisted
// flag for another event; the order's own snapshot wins unless the live flag
// is what turns selection off.
const resolveSelectionFlag = (
  order: RetouchingOrderState,
  orderKey: string,
  shootEventId: unknown,
  liveFlag: unknown,
): boolean => {
  const onCurrentEvent = belongsToCurrentEvent(order, orderKey, shootEventId);
  const resolvedFlag = onCurrentEvent
    ? liveFlag === true
    : order.additionalData?.retouchingRequiresImageSelection === true;
  return onCurrentEvent && liveFlag !== true
    ? false
    : order.productContext?.retouchingRequiresImageSelection ?? resolvedFlag;
};

// An opted-in, post-event order whose photos are all group photos: it cannot
// buy retouching at all (the server refuses even the flat add-on), so no
// retouching screen may be offered for it.
export const selectRetouchingGroupOnlyForOrder = createSelector(
  selectOrder,
  selectOrderKey,
  currentEventId,
  (state: RetouchingRootState) =>
    state.additionalData?.retouchingRequiresImageSelection,
  (order, orderKey, shootEventId, liveFlag): boolean =>
    order != null &&
    order.isPrepay !== true &&
    resolveSelectionFlag(order, orderKey, shootEventId, liveFlag) === true &&
    cartUtils.orderHasOnlyGroupPhotos(order),
);

const selectOrderConfigurationAvailable = (
  state: RetouchingRootState,
  orderKey: string | null,
): boolean =>
  selectConfigurationContext(state, resolveOrderKey(state, orderKey)).available;

// Whether any retouching affordance (the cart's Edit link, the catalog tile's
// picker, the pre-cart offer, the cart chip) may lead to adding or changing
// retouching on that order. Each condition is one addToCart or the retouching
// screen would otherwise refuse, so an affordance that passes never ends in a
// dialog or a dead end.
export const selectRetouchingAddableForOrder = createSelector(
  selectOrder,
  selectOrderKey,
  currentEventId,
  (state: RetouchingRootState) =>
    state.additionalData?.retouchingRequiresImageSelection,
  (state: RetouchingRootState) =>
    Boolean(
      state.v3Order?.retouchingCheckoutProtected ||
        state.v3Order?.retouchingCheckoutRequest ||
        state.v3Order?.retouchingQuoteError,
    ),
  selectRetouchingLevelsForOrder,
  selectOrderConfigurationAvailable,
  (
    order,
    orderKey,
    shootEventId,
    liveFlag,
    protectedAttempt,
    levels,
    contextAvailable,
  ): boolean => {
    if (!order || protectedAttempt || !contextAvailable) return false;

    const flag = resolveSelectionFlag(order, orderKey, shootEventId, liveFlag);

    return (
      cartUtils.canSelectRetouchingForOrder({
        order,
        requiresImageSelection: flag,
      }) &&
      levels.length > 0 &&
      !cartUtils.cartItemsIncludeRetouching(order.cartItems)
    );
  },
);
